import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button, Card, Empty, Modal, Segmented, Select, Space, Spin, Tag,
  TimePicker, Tooltip, Typography, message,
} from "antd";
import { EditOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import GxTable from "../../components/GxTable";
import GxNote from "../../components/GxNote";
import { getAttendanceWeeklyGrid, manualCorrectDay } from "../../api/client";
import { runWithAdminPin } from "../../utils/lockedMonth";
import { apiError } from "../../utils/apiError";
import { faNum, faDigits } from "../../utils/faNumberFormat";
import { formatDurationFa } from "../../utils/dateUtils";
import { filterByChildren } from "../../utils/selectFilter";

const { Text } = Typography;

/**
 * گریدِ هفتگیِ حضور — ردیف = پرسنل، ستون = روزهای هفته (شنبه به بعد).
 *
 * چرا هفتگی: ۳۱ ستون در یک صفحه جا نمی‌شود و کاربر عملاً هفته‌به‌هفته رسیدگی
 * می‌کند — همان فرمِ کاغذیِ کارگاه.
 *
 * ورود/خروجِ هر سلول همین‌جا قابلِ اصلاح است و اصلاح با `source=manual` ثبت
 * می‌شود، پس ایمپورتِ بعدیِ فایلِ دستگاه آن را بازنویسی نمی‌کند.
 */

const STATUS = {
  missing: { c: "#fff1f0", b: "#ffa39e", t: "غیبت/ثبت‌نشده" },
  incomplete: { c: "#fff7e6", b: "#ffd591", t: "ناقص — یک‌طرفه" },
  off: { c: "#fafafa", b: "#f0f0f0", t: "روزِ غیرکاری" },
  absent: { c: "#fff1f0", b: "#ffa39e", t: "غیبت" },
};

const hhmm = (m) => formatDurationFa(m || 0);

export default function WeeklyGrid({ year, month, locked }) {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(1);
  const [loading, setLoading] = useState(false);
  const [onlyEmp, setOnlyEmp] = useState(null);
  const [edit, setEdit] = useState(null);   // {row, day, cell}
  const [inT, setInT] = useState(null);
  const [outT, setOutT] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (w = week) => {
    setLoading(true);
    try {
      const r = await getAttendanceWeeklyGrid(year, month, w, onlyEmp || undefined);
      setData(r.data);
      setWeek(r.data.week);
    } catch (e) { message.error(apiError(e, "خطا در بارگذاری گرید")); }
    setLoading(false);
  }, [year, month, week, onlyEmp]);

  // با عوض‌شدنِ ماه از هفتهٔ اول شروع می‌شود؛ هفتهٔ ۵ ماهِ قبل در ماهِ جدید
  // ممکن است اصلاً وجود نداشته باشد.
  useEffect(() => { setWeek(1); load(1); }, [year, month, onlyEmp]);   // load عمداً وابستگی نیست

  const openEdit = (row, day) => {
    // ماهِ نهایی‌شده: ویرایش باز است؛ هنگامِ ثبت کدِ مدیریتی یا درخواستِ مدیر پرسیده می‌شود (v2.124.0)
    const cell = row.cells[day.date] || {};
    setEdit({ row, day, cell });
    setInT(cell.check_in ? dayjs(cell.check_in, "HH:mm") : null);
    setOutT(cell.check_out ? dayjs(cell.check_out, "HH:mm") : null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        employee_id: edit.row.employee_id,
        work_date: edit.day.date,
        check_in: inT ? inT.format("HH:mm:ss") : null,
        check_out: outT ? outT.format("HH:mm:ss") : null,
      };
      const out = await runWithAdminPin((pin) => manualCorrectDay(body, pin), { request: body });
      if (out?.requested) setEdit(null);
      else if (out) { message.success("اصلاح ثبت شد"); setEdit(null); load(week); }
    } catch (e) { message.error(apiError(e, "خطا در ثبتِ اصلاح")); }
    setSaving(false);
  };

  const columns = useMemo(() => {
    if (!data) return [];
    const dayCols = data.days.map((d) => ({
      title: (
        <div style={{ lineHeight: 1.35 }}>
          <div>{d.weekday_name}</div>
          <div style={{ fontSize: 11, opacity: 0.65 }}>{faNum(d.day)}</div>
        </div>
      ),
      key: d.date,
      width: 108,
      align: "center",
      render: (_, row) => {
        const c = row.cells[d.date] || {};
        const st = STATUS[c.status];
        const bad = c.counted_shortage > 0;
        return (
          <Tooltip title={
            <div style={{ fontSize: 12 }}>
              {st ? <div>{st.t}</div> : null}
              {c.late_minutes ? <div>دیرکرد: {hhmm(c.late_minutes)}</div> : null}
              {c.early_minutes ? <div>تعجیل: {hhmm(c.early_minutes)}</div> : null}
              {c.shortage_minutes ? <div>کسری: {hhmm(c.shortage_minutes)}
                {!c.counted_shortage ? " (زیرِ تلورانس — شمرده نشد)" : ""}</div> : null}
              {c.is_manual ? <div>اصلاحِ دستی</div> : null}
              <div style={{ opacity: 0.7 }}>برای اصلاح کلیک کنید</div>
            </div>
          }>
            <div
              onClick={() => openEdit(row, d)}
              style={{
                cursor: "pointer",
                background: st ? st.c : (bad ? "#fffbe6" : "transparent"),
                border: `1px solid ${st ? st.b : (bad ? "#ffe58f" : "transparent")}`,
                borderRadius: 6, padding: "3px 4px", minHeight: 34,
              }}
            >
              {c.check_in || c.check_out ? (
                <>
                  {/* جهتِ LTR روی خودِ ساعت‌ها: در RTL، «07:23» با دو نقطه
                      جابه‌جا رندر می‌شود و ورود و خروج قاطی می‌شوند. */}
                  <div style={{ fontSize: 12, whiteSpace: "nowrap",
                    direction: "ltr", unicodeBidi: "isolate" }}>
                    {faDigits(c.check_in || "—")}
                    <span style={{ opacity: 0.45, margin: "0 3px" }}>→</span>
                    {faDigits(c.check_out || "—")}
                  </div>
                  {c.counted_shortage > 0 && (
                    <div style={{ fontSize: 10, color: "#cf1322" }}>−{hhmm(c.counted_shortage)}</div>
                  )}
                  {c.is_manual && <div style={{ fontSize: 9, color: "#722ed1" }}>دستی</div>}
                </>
              ) : (
                <span style={{ fontSize: 11, opacity: 0.55 }}>{st ? st.t : "—"}</span>
              )}
            </div>
          </Tooltip>
        );
      },
    }));

    return [
      { title: "کد", dataIndex: "employee_code", width: 70, fixed: "right",
        render: (v) => faDigits(v) },
      { title: "نام", dataIndex: "full_name", width: 150, fixed: "right" },
      ...dayCols,
      { title: "کارکرد", dataIndex: "worked_minutes", width: 84, align: "center",
        render: (v) => hhmm(v) },
      { title: "دیرکرد", dataIndex: "late_minutes", width: 78, align: "center",
        render: (v) => (v ? <Text type="warning">{hhmm(v)}</Text> : "—") },
      { title: "کسرِ کار", dataIndex: "counted_shortage", width: 84, align: "center",
        render: (v, r) => (v
          ? <Tooltip title={`کسریِ خام: ${hhmm(r.shortage_minutes)}`}>
            <Text type="danger">{hhmm(v)}</Text></Tooltip>
          : "—") },
      { title: "غیبت", dataIndex: "missing_days", width: 64, align: "center",
        render: (v) => (v ? <Text type="danger">{faNum(v)}</Text> : "—") },
    ];
  }, [data, locked]);   // openEdit عمداً بیرون است؛ در هر رندر تازه ساخته می‌شود

  const weekOptions = (data?.weeks || []).map((w) => ({ value: w.index, label: w.label }));

  return (
    <Card
      title="گریدِ هفتگیِ حضور"
      extra={
        <Space wrap>
          <Select allowClear showSearch style={{ width: 200 }} placeholder="همهٔ پرسنل"
            value={onlyEmp} onChange={setOnlyEmp} optionFilterProp="children"
            filterOption={filterByChildren}
            options={(data?.rows || []).map((r) => ({
              value: r.employee_id, label: `${r.full_name} (${faDigits(r.employee_code)})` }))} />
          <Button icon={<ReloadOutlined />} onClick={() => load(week)} />
        </Space>
      }
    >
      <GxNote id="weekly-grid" signature="v1"
        message="روی هر سلول کلیک کنید تا ورود/خروجِ همان روز را اصلاح کنید">
        هفته‌ها از <b>شنبه</b> شروع می‌شوند. کسری‌ای که زیرِ آستانهٔ تلورانس باشد
        نشان داده می‌شود ولی جزوِ کسرِ کار حساب نمی‌شود — همان قاعده‌ای که در
        محاسبهٔ حقوق اجرا می‌شود. اصلاحِ دستی با ایمپورتِ دوبارهٔ فایلِ دستگاه
        بازنویسی نمی‌شود.
      </GxNote>

      <Space wrap style={{ marginBottom: 10 }}>
        {weekOptions.length > 0 && (
          <Segmented value={week} onChange={(v) => { setWeek(v); load(v); }}
            options={weekOptions} />
        )}
        {data && (
          <Tag color="blue">تلورانس: {faNum(data.tolerance_minutes)} دقیقه
            ({data.tolerance_scope === "monthly" ? "ماهانه" : "روزانه"})</Tag>
        )}
        {locked && <Tag color="red">ماه نهایی شده — اصلاح با کد مدیریتی یا تأییدِ مدیر</Tag>}
      </Space>

      <Spin spinning={loading}>
        {data?.rows?.length ? (
          <GxTable
            layoutId="att-weekly-grid"
            dataSource={data.rows}
            columns={columns}
            rowKey="employee_id"
            size="small"
            pagination={false}
            scroll={{ x: "max-content" }}
          />
        ) : (
          !loading && <Empty description="برای این هفته پرسنلی نیست" />
        )}
      </Spin>

      <Modal
        open={!!edit}
        title={edit ? `${edit.row.full_name} — ${edit.day.weekday_name} ${edit.day.jalali}` : ""}
        onCancel={() => setEdit(null)}
        onOk={save}
        confirmLoading={saving}
        okText="ثبتِ اصلاح" cancelText="انصراف"
        width={420}
      >
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          <Space>
            <span style={{ width: 46, display: "inline-block" }}>ورود:</span>
            <TimePicker value={inT} onChange={setInT} format="HH:mm" minuteStep={1}
              placeholder="--:--" />
            <span style={{ width: 46, display: "inline-block" }}>خروج:</span>
            <TimePicker value={outT} onChange={setOutT} format="HH:mm" minuteStep={1}
              placeholder="--:--" />
          </Space>
          {edit?.day?.start && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              ساعتِ کاریِ این روز: {faDigits(edit.day.start)} تا {faDigits(edit.day.end)}
              {" — "}موظفی: {hhmm(edit.day.expected_minutes)}
            </Text>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            هر دو خالی = غیبت. این اصلاح «دستی» ثبت می‌شود و با ایمپورتِ دوبارهٔ
            فایل پاک نمی‌شود.
          </Text>
        </Space>
      </Modal>
    </Card>
  );
}
